/**
 * OS status state machine — single source of truth for both API and mobile.
 *
 * The API enforces these transitions on `PATCH /service-orders/:id/status` and
 * on the sync push path. The mobile app uses the same map to enable/disable
 * UI controls so the user can't even attempt an invalid move.
 *
 * Special-case endpoints that bypass this map:
 *   - `POST /service-orders/:id/payment` jumps any non-final state to
 *     `completed` — but ONLY when the payment settles what is owed
 *     (total − discount). A partial payment is recorded and the OS stays
 *     where it is, typically `waiting_payment`.
 *   - `POST /service-orders/:id/uncancel` resurrects `cancelled` →
 *     `in_analysis` within a 30-day window (manager only).
 *   - `PATCH /backoffice/service-orders/:id/status` — correção feita pelo
 *     suporte MustAuto (staff), para qualquer status, com motivo obrigatório.
 *     Grava {@link OS_STATUS_CORRECTED_ACTION} no `os_audit_log`.
 *   - `POST /service-orders/:id/reopen` — o gestor desfaz o fechamento
 *     (`completed`/`delivered`) em até 48h, de volta ao status anterior.
 */

/**
 * Ação do `os_audit_log` para a correção de status feita pelo backoffice.
 * Distinta de `status_changed` de propósito: a mudança não passou pela máquina
 * de estados nem foi feita por alguém da oficina, e o histórico da OS precisa
 * dizer isso a quem ler depois ("quem reabriu minha OS?"). Toda timeline que
 * lista transições de status inclui esta ação junto de `status_changed`.
 */
export const OS_STATUS_CORRECTED_ACTION = 'status_corrected';

/**
 * Ação do `os_audit_log` para a reabertura feita pelo GESTOR da oficina
 * (`POST /service-orders/:id/reopen`): desfaz o fechamento por engano dentro
 * de {@link OS_REOPEN_WINDOW_HOURS}, de volta ao status em que a OS estava
 * antes de fechar. Como a correção, entra em toda timeline de status.
 */
export const OS_REOPENED_ACTION = 'reopened';

/** Janela, a partir do fechamento, em que o gestor ainda pode reabrir a OS. */
export const OS_REOPEN_WINDOW_HOURS = 48;

/** Status que o gestor pode reabrir. `cancelled` tem o próprio `/uncancel`. */
export const OS_REOPENABLE_STATUSES = ['completed', 'delivered'] as const satisfies readonly OSStatus[];

export const OS_STATUSES = [
  'in_analysis',
  'in_execution',
  'waiting_part',
  'outsourced',
  'completed',
  'waiting_payment',
  'cancelled',
  'delivered',
] as const;

export type OSStatus = (typeof OS_STATUSES)[number];

/**
 * Statuses em que a OS não está mais em fluxo de trabalho ativo: serviço
 * concluído (`completed`), cancelado (`cancelled`) ou já entregue ao cliente
 * (`delivered`, estado terminal). Use como filtro único para "OS ativas" —
 * ex.: alerta de OS parada, contadores de dashboard, digest.
 *
 * `delivered` é terminal (sem transições de saída): uma OS entregue nunca mais
 * é tocada, então seu `updated_at` congela. Esquecê-la desta lista faz toda OS
 * entregue ser contada como "parada há mais de 24h" indefinidamente.
 */
export const OS_CLOSED_STATUSES = ['completed', 'cancelled', 'delivered'] as const satisfies readonly OSStatus[];

export const OS_STATUS_TRANSITIONS: Record<OSStatus, readonly OSStatus[]> = {
  // `in_analysis → outsourced` é DELIBERADO, e já foi questionado uma vez
  // (relato de campo, set/2026: "posso mandar o veículo para terceiro com OS
  // em análise"). Existe o caso em que a oficina sabe de cara para onde o
  // carro vai — a batida que segue direto para a funilaria — e travar a
  // aresta pararia essa oficina para proteger um descuido. A proteção mora na
  // tela: o app confirma antes de marcar o terceirizado como "Enviado" com a
  // OS ainda em análise (`(mechanic)/os/[id].tsx`), avisando em vez de
  // bloquear. Guarda desta decisão: `tests/feature/os-closed-statuses.test.ts`.
  in_analysis: ['in_execution', 'waiting_part', 'outsourced', 'cancelled'],
  // 'Finalizar Serviço' (botão principal do mecânico em execução) leva direto
  // para waiting_payment — mecânico terminou o trabalho, OS aguarda pagamento.
  // O fluxo do produto é in_execution → waiting_payment → completed (via
  // POST /payment) → delivered. A aresta para waiting_payment faltava aqui, e
  // o sync push descartava a transição como inválida (loop: device mostrava a
  // mudança, servidor recusava, pull revertia). 'completed' segue válido para
  // quem registra a conclusão antes do pagamento.
  in_execution: ['waiting_part', 'outsourced', 'completed', 'waiting_payment', 'cancelled'],
  waiting_part: ['in_execution', 'outsourced', 'cancelled'],
  outsourced: ['in_execution', 'waiting_part', 'completed', 'cancelled'],
  completed: ['delivered', 'waiting_payment'],
  // Audit 02-M5: waiting_payment era terminal — OS ficava presa quando
  // o pagamento entrava parcial ou cliente pediu pra retomar serviço.
  // Permite retorno para completed (pagamento confirmado) e delivered
  // (entrega após acordo de pagamento posterior).
  waiting_payment: ['completed', 'delivered', 'cancelled'],
  cancelled: [],
  delivered: [],
};

export function isOSStatus(value: unknown): value is OSStatus {
  return typeof value === 'string' && (OS_STATUSES as readonly string[]).includes(value);
}

/**
 * Returns true when `from === to` (idempotent no-op) or when `to` is in the
 * allowed transitions list for `from`. Unknown states return false.
 */
export function canTransitionOSStatus(from: OSStatus, to: OSStatus): boolean {
  if (from === to) return true;
  const allowed = OS_STATUS_TRANSITIONS[from];
  return allowed?.includes(to) ?? false;
}

/**
 * A OS já saiu do fluxo de trabalho ativo? Guarda de leitura única para
 * {@link OS_CLOSED_STATUSES} — aceita `string` de propósito, porque quase todo
 * consumidor (resposta de API, linha do WatermelonDB, `params` de rota) tem o
 * status como texto solto e a alternativa era um `includes` reescrito por tela.
 *
 * Uma OS fechada não é "parada" nem "atrasada": o ciclo dela acabou. Toda tela
 * que fala de atraso, e toda contagem de "OS ativa", passa por aqui.
 */
export function isOSClosed(status: string | null | undefined): boolean {
  return status != null && (OS_CLOSED_STATUSES as readonly string[]).includes(status);
}

/**
 * Statuses em que não existe mais entrega pendente ao cliente: o carro saiu da
 * oficina (`delivered`) ou a OS foi anulada (`cancelled`).
 */
export const OS_NO_PENDING_DELIVERY_STATUSES = ['delivered', 'cancelled'] as const satisfies readonly OSStatus[];

/**
 * A OS ainda pode estar ATRASADA com o cliente?
 *
 * Recorte deliberadamente diferente de {@link isOSClosed}, e a diferença é
 * decisão de produto (set/2026), não detalhe de implementação. `completed` e
 * `waiting_payment` são "serviço terminado" e saem de toda contagem de OS
 * ativa — mas o carro continua no pátio, e o que foi combinado com o cliente
 * era a data de ENTREGA. Passou a data com o carro ainda lá, está atrasado, e
 * é o cliente quem sente. Só quando o carro vai embora, ou a OS é anulada, é
 * que não há mais prazo a cumprir.
 */
export function canOSBeLate(status: string): boolean {
  return !(OS_NO_PENDING_DELIVERY_STATUSES as readonly string[]).includes(status);
}

/**
 * As anotações internas da OS ainda podem mudar?
 *
 * O mesmo recorte do atraso, e pelo mesmo motivo: enquanto o carro está na
 * oficina — inclusive concluído ou esperando pagamento — ainda pode haver o que
 * anotar para a retirada. Entregue ou cancelada, a anotação vira registro.
 * Vale na rota, no sync e nas duas telas.
 */
export function canEditInternalNotes(status: string): boolean {
  return canOSBeLate(status);
}

/** Teto do texto das anotações internas — a rota recusa acima; as telas cortam. */
export const INTERNAL_NOTES_MAX = 5000;

/**
 * A anotação como é gravada: sem espaços nas pontas, e vazia vira `null` ("sem
 * anotação" — é como a abertura grava). Rota, sync e as duas telas comparam por
 * aqui, senão o texto que só difere num espaço vira mudança no histórico.
 */
export function normalizeInternalNotes(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * O serviço já terminou e falta só o pagamento e/ou a retirada.
 *
 * Serve para não fazer PREVISÃO onde não cabe: num carro que já está pronto,
 * "Pronto em ~3 dias" é previsão de algo que já aconteceu. Nesse estado a
 * única coisa verdadeira a dizer sobre o prazo é o atraso, quando houver.
 */
export const OS_SERVICE_DONE_STATUSES = ['completed', 'waiting_payment'] as const satisfies readonly OSStatus[];

export function isOSServiceDone(status: string): boolean {
  return (OS_SERVICE_DONE_STATUSES as readonly string[]).includes(status);
}
